import React, { useState } from 'react';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useDragControls,
  animate,
  PanInfo,
} from 'motion/react';
import {
  ShoppingCart,
  X,
  Plus,
  Minus,
  Trash2,
  CheckCircle,
  Circle,
  Sparkles,
} from 'lucide-react';
import { img_url } from '../../api/index';
import { useBodyScrollLock } from './useBodyScrollLock';
import { useQrLanguage } from './LanguageContext';

/** Shape of a cart row.  Defined locally to avoid a circular type import
 *  with QRMenuPage (which imports Cart). */
export interface CartItem {
  id: string | number;
  detail_id?: string | number | null;
  name: string;
  price: number | string;
  quantity: number;
  image?: string | null;
}

interface CartProps {
  items: CartItem[];
  isOpen: boolean;
  onClose: () => void;
  onUpdateQuantity: (
    id: string | number,
    quantity: number,
    detailId?: string | number | null,
  ) => void;
  onRemoveItem: (id: string | number, detailId?: string | number | null) => void;
  onCheckout: () => void;
  currency?: string;
}

const getImageUrl = (img?: string | null) => {
  if (!img) return '';
  return img.startsWith('http') ? img : `${img_url}/${img}`;
};

const formatPrice = (price: number | string) => {
  const n = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(n)) return '0.00';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Light haptic feedback helper (no-op on iOS Safari, works on Android Chrome)
const haptic = (ms = 8) => {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      (navigator as any).vibrate(ms);
    }
  } catch {
    /* noop */
  }
};

export default function Cart({
  items,
  isOpen,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  currency = '₼',
}: CartProps) {
  const { t } = useQrLanguage();
  const [isSocialPayMode, setIsSocialPayMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Array<string | number>>([]);
  const dragControls = useDragControls();
  const sheetY = useMotionValue(0);
  const backdropOpacity = useTransform(sheetY, [0, 400], [1, 0]);

  useBodyScrollLock(isOpen);

  const total = items.reduce(
    (sum, item) => sum + parseFloat(String(item.price)) * item.quantity,
    0,
  );
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const selectedTotal = items
    .filter((item) => selectedItems.includes(item.id))
    .reduce((sum, item) => sum + parseFloat(String(item.price)) * item.quantity, 0);

  const toggleItemSelection = (id: string | number) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const closeAndReset = () => {
    onClose();
    // Reset for next open after the exit animation finishes
    setTimeout(() => {
      sheetY.set(0);
      setIsSocialPayMode(false);
      setSelectedItems([]);
    }, 60);
  };

  /** Native iOS-style drag-to-dismiss. Triggered only from the drag handle
   *  + header (so the inner item list still scrolls naturally). */
  const handleDragEnd = (_: any, info: PanInfo) => {
    const shouldClose =
      info.offset.y > 140 || (info.velocity.y > 500 && info.offset.y > 30);
    if (shouldClose) {
      haptic(8);
      closeAndReset();
    } else {
      animate(sheetY, 0, {
        type: 'spring',
        stiffness: 380,
        damping: 30,
        mass: 0.6,
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-[80] qrm-no-select"
          style={{ willChange: 'opacity' }}
        >
          {/* Backdrop — fades with the drag offset for a native feel */}
          <motion.div
            onClick={() => {
              haptic(6);
              closeAndReset();
            }}
            style={{ opacity: backdropOpacity }}
            className="absolute inset-0 bg-black/55 backdrop-blur-md"
          />

          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 450, damping: 35, mass: 0.8 }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.55 }}
            dragMomentum={false}
            onDragEnd={handleDragEnd}
            style={{ y: sheetY, willChange: 'transform' }}
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-[#1a1a1a] rounded-t-[28px] shadow-[0_-12px_40px_rgba(0,0,0,0.18)] dark:shadow-[0_-12px_40px_rgba(0,0,0,0.6)] max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Drag handle area — touching here starts the drag.  The handle
                also doubles as the visible "pill" indicator. */}
            <div
              onPointerDown={(e) => dragControls.start(e)}
              className="pt-3 pb-2 cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
            >
              <div className="w-11 h-[5px] bg-gray-300 dark:bg-white/20 rounded-full mx-auto" />
            </div>

            {/* Header — also a drag source */}
            <div
              onPointerDown={(e) => dragControls.start(e)}
              className="flex items-center justify-between px-6 pb-4 cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
            >
              <div>
                <h2 className="text-[22px] font-black text-gray-900 dark:text-white tracking-tight leading-tight">
                  {t.myCart}
                </h2>
                <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 mt-0.5">
                  {itemCount > 0 ? t.productsCount(itemCount) : t.cartEmpty}
                </p>
              </div>
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => {
                  haptic(6);
                  closeAndReset();
                }}
                className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/15 active:scale-90 transition-all"
                aria-label="close"
              >
                <X className="w-5 h-5 text-gray-700 dark:text-gray-200" />
              </button>
            </div>

            {/* Items - scrollable */}
            <div className="flex-1 qrm-modal-scroll scrollbar-hide overflow-y-auto px-5 pb-4">
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 flex items-center justify-center qrm-scale-in">
                    <ShoppingCart className="w-9 h-9 text-orange-400 dark:text-orange-500" />
                  </div>
                  <p className="text-base font-bold text-gray-900 dark:text-white mb-1">
                    {t.emptyCartTitle}
                  </p>
                  <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                    {t.emptyCartSubtitle}
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {items.map((item, idx) => {
                    const itemImg = getImageUrl(item.image);
                    const isSel = selectedItems.includes(item.id);
                    return (
                      <motion.div
                        key={`${item.id}-${item.detail_id || ''}`}
                        initial={{ opacity: 0, y: 12, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, x: -40, transition: { duration: 0.15 } }}
                        transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 35,
                        }}
                        onClick={() => isSocialPayMode && toggleItemSelection(item.id)}
                        className={`flex gap-3 p-3 rounded-2xl transition-colors ${isSocialPayMode
                          ? `cursor-pointer ${isSel
                            ? 'bg-orange-50 dark:bg-orange-500/10 ring-2 ring-orange-500'
                            : 'bg-gray-50 dark:bg-white/5'
                          }`
                          : 'bg-gray-50 dark:bg-white/5'
                          }`}
                      >
                        {isSocialPayMode && (
                          <div className="flex items-center shrink-0">
                            {isSel ? (
                              <CheckCircle className="w-6 h-6 text-orange-500" />
                            ) : (
                              <Circle className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                            )}
                          </div>
                        )}
                        <div className="w-[68px] h-[68px] rounded-2xl overflow-hidden shrink-0 bg-gray-200 dark:bg-white/10">
                          {itemImg ? (
                            <img
                              src={itemImg}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-2xl">
                              🍽️
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[14px] text-gray-900 dark:text-white line-clamp-2 leading-tight mb-1">
                            {item.name}
                          </h4>
                          <p className="text-orange-500 dark:text-orange-400 font-black text-[15px] tracking-tight">
                            {currency}
                            {formatPrice(parseFloat(String(item.price)) * item.quantity)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end justify-between">
                          {!isSocialPayMode && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                haptic(8);
                                onRemoveItem(item.id, item.detail_id);
                              }}
                              className="text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors p-1 active:scale-90"
                              aria-label="remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {!isSocialPayMode && (
                            <div className="flex items-center gap-1.5 bg-white dark:bg-[#0f0f10] rounded-full border border-gray-200 dark:border-white/10 px-1 py-1 shadow-sm">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  haptic(6);
                                  onUpdateQuantity(
                                    item.id,
                                    Math.max(0, item.quantity - 1),
                                    item.detail_id,
                                  );
                                }}
                                className="w-7 h-7 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/15 active:scale-90 transition-all"
                                aria-label="minus"
                              >
                                <Minus className="w-3 h-3 text-gray-700 dark:text-gray-200" />
                              </button>
                              <span className="font-bold text-[13px] w-5 text-center text-gray-900 dark:text-white">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  haptic(6);
                                  onUpdateQuantity(
                                    item.id,
                                    item.quantity + 1,
                                    item.detail_id,
                                  );
                                }}
                                className="w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 active:scale-90 transition-all shadow-sm shadow-orange-500/30"
                                aria-label="plus"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-gray-100 dark:border-white/5 px-5 pt-4 pb-5 qrm-safe-bottom bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-2xl">
                <button
                  type="button"
                  onClick={() => {
                    haptic(8);
                    setIsSocialPayMode(!isSocialPayMode);
                    setSelectedItems([]);
                  }}
                  className={`w-full py-2.5 mb-3 text-[13px] font-bold rounded-xl border-2 transition-colors flex items-center justify-center gap-2 active:scale-[0.98] ${isSocialPayMode
                    ? 'bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/30'
                    : 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-orange-300 dark:hover:border-orange-500/30'
                    }`}
                >
                  <Sparkles className="w-4 h-4" />
                  {isSocialPayMode ? t.cancelSelection : t.paySplit}
                </button>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {isSocialPayMode ? t.selected : t.total}
                  </span>
                  <span className="text-[26px] font-black text-orange-500 tracking-tight">
                    {currency}
                    {isSocialPayMode ? formatPrice(selectedTotal) : formatPrice(total)}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    haptic(12);
                    onCheckout();
                    closeAndReset();
                  }}
                  className="w-full h-[54px] rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black text-[15px] tracking-wide shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {isSocialPayMode ? t.paySelected : t.sendOrder}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
